import { message } from 'antd';
import type { AxiosResponse } from 'axios';
import uniq from 'lodash/uniq';
import Router from 'next/router';
import {
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import { LedgerItemType } from '@evenfinancial/finance-client';
import type {
  AccountAdjustment,
  AccountSettlement,
  LedgerItemEvent,
  LedgerItemRecreationBatch,
  PayableSummary,
} from '@evenfinancial/finance-client';

import { client } from '@portal/api-client/src/client';
import type { PortalLedgerItem } from '@portal/api-models';
import { getPortalError } from '@portal/common';

import { Resource, setIsLoadingAction } from '../loading';
import { richAccessTokenByUuidRequestAction } from '../user/actions';

import { PayableActionType } from './actionTypes';
import * as actions from './actions';
import { selectPayableById, selectUniqAdjustmentUuids } from './selectors';
import type { AccountAddAdjustmentsForm } from './types';

export function* getPayables() {
  try {
    const { data: payables }: AxiosResponse<PayableSummary[]> = yield call(
      [client, 'get'],
      '/finance/ledgerItems/payableSummaries',
      {
        params: {
          limit: 8000,
          order: 'desc',
        },
      }
    );

    if (!payables?.length) {
      throw 'No payables returned from the backend';
    }

    yield put(actions.payableRequestAction.success(payables));
  } catch (err: any) {
    yield put(actions.payableRequestAction.failure(err));
  }
}

export function* getPayableById(
  action: ActionType<typeof actions.payableByIdRequestAction.request>
) {
  try {
    yield put(setIsLoadingAction([Resource.PayableById, true]));
    const { data: payable }: AxiosResponse<PortalLedgerItem> = yield call(
      [client, 'get'],
      `/finance/ledgerItems/${action.payload.id}`
    );

    if (!payable?.id) {
      throw 'No payables returned from the backend';
    }

    yield put(actions.payableByIdRequestAction.success(payable));
  } catch (err: any) {
    yield put(actions.payableByIdRequestAction.failure(err));
  } finally {
    yield put(setIsLoadingAction([Resource.PayableById, false]));
  }
}

export function* getAdjustments() {
  try {
    const params = {
      ledgerItemType: LedgerItemType.Payable,
    };

    const { data: adjustments }: AxiosResponse<AccountAdjustment[]> =
      yield call(client.get, '/finance/accountAdjustments', { params });

    yield put(actions.payableAdjustmentsRequestAction.success(adjustments));

    const uuids: string[] = yield select(selectUniqAdjustmentUuids);

    yield put(
      richAccessTokenByUuidRequestAction.request({
        uuids,
      })
    );
  } catch (err: any) {
    yield put(actions.payableAdjustmentsRequestAction.failure(err));
  }
}

export function* getAdjustmentsByLedgerItemId(
  action: ActionType<
    typeof actions.payableAdjustmentsByLedgerItemIdRequestAction.request
  >
) {
  try {
    const { ledgerItemId } = action.payload;

    const params = {
      ledgerItemIds: [ledgerItemId],
      ledgerItemType: LedgerItemType.Payable,
    };

    const { data: adjustments }: AxiosResponse<AccountAdjustment[]> =
      yield call([client, 'get'], '/finance/accountAdjustments', { params });

    yield put(
      actions.payableAdjustmentsByLedgerItemIdRequestAction.success({
        adjustments,
        ledgerItemId,
      })
    );

    const uuids = uniq(adjustments.map((a) => a.createdBy));

    yield put(
      richAccessTokenByUuidRequestAction.request({
        uuids,
      })
    );
  } catch (err: any) {
    yield put(
      actions.payableAdjustmentsByLedgerItemIdRequestAction.failure(err)
    );
  }
}

export function* ignorePayable(
  action: ActionType<typeof actions.payableIgnoreAction.request>
) {
  try {
    const { data: response }: AxiosResponse<PortalLedgerItem[]> = yield call(
      [client, 'post'],
      '/finance/ledgerItems/create_ledger_item_reconciliation',
      {
        ledgerItemIds: [action.payload.id],
        memo: '',
        reconciliationType: action.payload.reconciliationType,
      }
    );

    yield put(actions.payableIgnoreAction.success(response));

    yield delay(100);
    yield call(
      message.success,
      'Ledger item reconciliation updated successfully'
    );
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.payableIgnoreAction.failure(portalError));
  }
}

export function* deleteAdjustment(
  action: ActionType<typeof actions.payableAdjustmentDeleteAction.request>
) {
  try {
    yield call(
      [client, 'delete'],
      `/finance/accountAdjustments/${action.payload.id}`
    );

    yield put(actions.payableAdjustmentDeleteAction.success(action.payload));

    yield delay(100);
    yield call(
      [message, 'success'],
      'Payable adjustment successfully cancelled.'
    );
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.payableAdjustmentDeleteAction.failure(portalError));
  }
}

export function* deletePayable(
  action: ActionType<typeof actions.deletePayableAction.request>
) {
  try {
    yield call(
      [client, 'delete'],
      `/finance/ledgerItems/ledger_item_delete/${action.payload.id}`
    );

    yield call([Router, 'replace'], `${location.origin}/billing/payables`);
    yield put(actions.deletePayableAction.success({ id: action.payload.id }));

    yield delay(100);
    yield call([message, 'success'], 'The payable was deleted');
  } catch (err: any) {
    yield put(actions.deletePayableAction.failure(err));
  }
}

export function* regenerateLedgerItems(
  action: ActionType<typeof actions.regenerateLedgerItemsRequestAction.request>
) {
  try {
    const { ledgerItemIds, copyAdjustments } = action.payload;

    const {
      data: ledgerItemsRegenerateData,
    }: AxiosResponse<LedgerItemRecreationBatch> = yield call(
      [client, 'post'],
      '/finance/ledgerItems/regenerate',
      {
        copyAdjustments,
        ledgerItemIds,
      }
    );

    yield put(
      actions.regenerateLedgerItemsRequestAction.success(
        ledgerItemsRegenerateData
      )
    );

    yield delay(100);
    yield call([message, 'success'], 'Payables regenerated successfully');
  } catch (e: any) {
    yield put(actions.regenerateLedgerItemsRequestAction.failure(e));
  }
}

export function* settleLedgerItems(
  action: ActionType<typeof actions.settleLedgerItemsRequestAction.request>
) {
  try {
    const { accountIds } = action.payload;

    const { data: ledgerItemSettlements }: AxiosResponse<AccountSettlement[]> =
      yield call([client, 'post'], '/finance/ledgerItems/settlement', {
        accountIds,
      });

    yield put(
      actions.settleLedgerItemsRequestAction.success(ledgerItemSettlements)
    );
    yield delay(100);
    yield call([message, 'success'], 'Accounts reconciled successfully');
  } catch (e: any) {
    yield put(actions.settleLedgerItemsRequestAction.failure(e));
  }
}

export function* createOneOffLedgerItem(
  action: ActionType<typeof actions.createOneOffLedgerItemRequestAction.request>
) {
  try {
    const { accountId, activityPeriod, dueDate, details } = action.payload;

    const [startDate, endDate] = activityPeriod;

    const { data: oneOffLedgerItem }: AxiosResponse<PortalLedgerItem> =
      yield call([client, 'post'], '/finance/ledgerItems/oneOffs', {
        accountId,
        details,
        dueDate,
        endDate,
        startDate,
      });

    yield put(
      actions.createOneOffLedgerItemRequestAction.success(oneOffLedgerItem)
    );
    yield call([Router, 'push'], `/billing/payables/${oneOffLedgerItem.id}`);

    yield delay(100);
    yield call([message, 'success'], 'Payable created successfully');
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.createOneOffLedgerItemRequestAction.failure(portalError));
    yield delay(100);
    yield call([message, 'error'], portalError.customError);
  }
}

export function* addAdjustments(
  action: ActionType<typeof actions.payableAddAdjustmentAction.request>
) {
  try {
    const { accountId, ledgerItemId, adjustments } = action.payload;
    const adjustmentForm: AccountAddAdjustmentsForm[] | undefined =
      adjustments?.map((adjustment) => ({
        ...adjustment,
        accountId,
        ledgerItemId,
      }));

    if (adjustmentForm) {
      const {
        data: accountAdjustmentsData,
      }: AxiosResponse<AccountAdjustment[]> = yield call(
        [client, 'post'],
        '/finance/accountAdjustments',
        adjustmentForm
      );

      yield put(
        actions.payableAddAdjustmentAction.success(accountAdjustmentsData)
      );
    }

    yield delay(100);
    yield call([message, 'success'], 'Successfully added account adjustments.');
  } catch (e: any) {
    yield put(actions.payableAddAdjustmentAction.failure(e));
  }
}

export function* getPayableEventsById(
  action: ActionType<typeof actions.payableEventsByIdRequestAction.request>
) {
  try {
    const { data: events }: AxiosResponse<LedgerItemEvent[]> = yield call(
      [client, 'get'],
      `/finance/ledgerItems/${action.payload.id}/events`
    );

    yield put(actions.payableEventsByIdRequestAction.success(events));
  } catch (err: any) {
    yield put(actions.payableEventsByIdRequestAction.failure(err));
  }
}

export function* getPayableDetailPage(
  action: ActionType<typeof actions.payableDetailPage>
) {
  try {
    const {
      payload: { id },
    } = action;

    yield call(getPayableById, {
      payload: { id },
      type: PayableActionType.GET_BY_ID_REQUEST,
    });

    const payable: PortalLedgerItem | undefined = yield select((state) =>
      selectPayableById(state, id)
    );

    if (payable) {
      yield put(actions.payableEventsByIdRequestAction.request({ id }));
      yield put(
        actions.payableAdjustmentsByLedgerItemIdRequestAction.request({
          ledgerItemId: id,
        })
      );
    }
    // eslint-disable-next-line no-empty
  } catch {}
}

export function* payableSaga() {
  yield takeLatest(
    PayableActionType.GET_EVENTS_BY_ID_REQUEST,
    getPayableEventsById
  );

  yield takeLatest(PayableActionType.GET_DETAIL_PAGE, getPayableDetailPage);

  yield takeLatest(PayableActionType.GET_ALL_REQUEST, getPayables);

  yield takeLatest(PayableActionType.GET_BY_ID_REQUEST, getPayableById);

  yield takeLatest(
    PayableActionType.GET_ALL_ADJUSTMENTS_REQUEST,
    getAdjustments
  );

  yield takeLatest(
    PayableActionType.GET_ADJUSTMENTS_BY_LEDGER_ITEM_ID_REQUEST,
    getAdjustmentsByLedgerItemId
  );

  yield takeLatest(PayableActionType.IGNORE_PAYABLE_REQUEST, ignorePayable);

  yield takeLatest(
    PayableActionType.DELETE_ADJUSTMENT_REQUEST,
    deleteAdjustment
  );

  yield takeLatest(PayableActionType.DELETE_PAYABLE_REQUEST, deletePayable);

  yield takeEvery(
    PayableActionType.REGENERATE_LEDGER_ITEMS_REQUEST,
    regenerateLedgerItems
  );

  yield takeEvery(
    PayableActionType.SETTLE_LEDGER_ITEMS_REQUEST,
    settleLedgerItems
  );

  yield takeLatest(
    PayableActionType.CREATE_ONE_OFF_LEDGER_ITEM_REQUEST,
    createOneOffLedgerItem
  );

  yield takeLatest(PayableActionType.ADD_ADJUSTMENT_REQUEST, addAdjustments);
}
